'use client';

import { useState, type CSSProperties } from 'react';
import Image from 'next/image';
import { cn } from '@sharpr/ui';

interface PlayerAvatarProps {
  name: string;
  imageUrl?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  style?: CSSProperties;   // e.g. a coloured ring (box-shadow)
}

const sizeClasses = {
  /** Filter chips. */
  xs: 'w-4 h-4',
  sm: 'w-7 h-7',
  md: 'w-9 h-9 sm:w-10 sm:h-10',
  lg: 'w-12 h-12',
  /** Hero-header size for the line-movement overlay. */
  xl: 'w-16 h-16',
} as const;

const imageSizes = {
  xs: 16,
  sm: 28,
  md: 40,
  lg: 48,
  xl: 64,
} as const;

const initialsSizes = {
  xs: 'text-[6px]',
  sm: 'text-[10px]',
  md: 'text-xs sm:text-sm',
  lg: 'text-sm',
  xl: 'text-base',
} as const;

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

// A player's headshot at a fixed size (next/image serves it resized), or his initials when there is none or it fails.
export function PlayerAvatar({ name, imageUrl, size = 'md', className, style }: PlayerAvatarProps) {
  const px = imageSizes[size];
  const [failed, setFailed] = useState<string | null>(null);

  return (
    <div
      className={cn(
        'relative flex-shrink-0 rounded-full overflow-hidden border border-border dark:border-border-secondary bg-muted dark:bg-bg-quaternary',
        sizeClasses[size],
        className
      )}
      style={style}
    >
      {imageUrl && failed !== imageUrl ? (
        <Image
          src={imageUrl}
          alt={name}
          width={px}
          height={px}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setFailed(imageUrl)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <span className={cn('font-semibold text-muted-foreground dark:text-text-tertiary', initialsSizes[size])}>
            {getInitials(name)}
          </span>
        </div>
      )}
    </div>
  );
}
